import { Dispatch } from 'react-redux';
import { AxiosError } from "axios";
import { AuthActionCreators, IApplicationState } from "../";
import {
    IContact, IListItem, IBaseResponse, IContactRequest, IEmailFlag, IContactForList, IContactEntityTypePositions,
    ILogisticsProvider, ICenterAddress, IEmailFlagGroup
} from "@nkr/models/index";
import { IPost, IGet } from '../authorization';
import { IDelete, IPut } from '../authorization/actions';

export interface ILookupData {
    phoneTypes: IListItem[],
    mobileProviders: IListItem[],
    emailFlags: IEmailFlag[],
    emailFlagGroups: IEmailFlagGroup[],
    contactEntityTypes: IListItem[],
    positions: IContactEntityTypePositions[],
    swapCouriers: ILogisticsProvider[],
    centerAddresses: ICenterAddress[]
}

export const ActionTypes = {
    SET_CONTACT_LIST: "SET_CONTACT_LIST",
    SET_CONTACT_POSITION_LIST: "SET_CONTACT_POSITION_LIST",
    SET_CONTACT_LOOKUP_DATA: "SET_CONTACT_LOOKUP_DATA",
    SET_CONTACT_INFO: "SET_CONTACT_INFO",
}

export interface IContactListResponse {
    data: {
        success: boolean,
        message: string,
        results: IContactForList[]
    }
}

export interface IContactFullListResponse {
    data: {
        success: boolean,
        message: string,
        results: IContact[]
    }
}

export interface IContactResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            contact: IContact,
            allTags: string[]
        }
    }
}

export interface IContactPositionListResponse {
    data: {
        success: boolean,
        message: string,
        results: IListItem[]
    }
}

export interface IContactLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: ILookupData
    }
}

export const ActionCreators = {
    getContactList: (organType: number, centerId?: number, type?: number, search?: string, roles?: { entityType: number, roleId: number }[], includeDeleted?: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IContactListResponse> => {
        let url = `${__API__}/contact/list`;

        let options = {
            url,
            data: {
                organType: organType == 0 ? null : organType,
                centerId: centerId > 0 ? centerId : null,
                type: type > 0 ? type : null,
                search,
                roles,
                includeDeleted
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IContactListResponse) => {
                if (response.data.success)
                    dispatch(ActionCreators.setContactList(response.data.results));

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    getFullList: (entityType: number, entityTypeId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IContactFullListResponse> => {
        let options = {
            url: `${__API__}/contact/full?entityType=${entityType}&entityTypeId=${entityTypeId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getContacts: (search: string) => (dispatch: Dispatch<IApplicationState>): Promise<IContactListResponse> => {
        let options = {
            url: `${__API__}/contact/search/${search}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IContactListResponse) => {
                if (response.data.success)
                    dispatch(ActionCreators.setContactList(response.data.results));

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setContactList: (list: IContactForList[]) => {
        return {
            type: ActionTypes.SET_CONTACT_LIST,
            list
        };
    },
    getContact: (id: number) => (dispatch: Dispatch<IApplicationState>): Promise<IContactResponse> => {
        let options = {
            url: __API__ + `/contact/${id}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IContactResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setContact: (contact: IContact) => {
        return {
            type: ActionTypes.SET_CONTACT_INFO,
            contact
        };
    },
    saveContact: (request: IContactRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IContactResponse> => {
        if (request.id == 0) {
            let options: IPost = {
                url: __API__ + '/contact',
                data: request
            };

            return await dispatch(AuthActionCreators.apiPost(options));
        } else {
            let options: IPut = {
                url: __API__ + '/contact',
                data: request
            };

            return await dispatch(AuthActionCreators.apiPut(options));
        }
    },
    deleteContact: (contactId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IDelete = {
            url: `${__API__}/contact/${contactId}`
        };

        return await dispatch(AuthActionCreators.apiDelete(options));
    },
    hardDeleteContact: (contactId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IDelete = {
            url: `${__API__}/contact/hard-delete/${contactId}`
        };

        return await dispatch(AuthActionCreators.apiDelete(options));
    },
    hardDeleteContactsBulk: (contactIds: number[]) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        // Convert number[] to long[] to match backend expectation
        const contactIdsAsLong = contactIds.map(id => Number(id));

        let options: IPost = {
            url: `${__API__}/contact/hard-delete-bulk`,
            data: contactIdsAsLong
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    getContactPositions: () => (dispatch: Dispatch<IApplicationState>): Promise<IContactPositionListResponse> => {
        let options = {
            url: __API__ + `/contact/position`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IContactPositionListResponse) => {
                if (response.data.success) {
                    dispatch(ActionCreators.setContactPositionList(response.data.results));
                }

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setContactPositionList: (list: IListItem[]) => {
        return {
            type: ActionTypes.SET_CONTACT_POSITION_LIST,
            list
        };
    },
    getContactLookupData: (centerId: number, organType: number) => (dispatch: Dispatch<IApplicationState>): Promise<IContactLookupDataResponse> => {
        let options = {
            url: `${__API__}/contact/lookup-data?centerId=${centerId}&organType=${organType}`
        };

        return dispatch(AuthActionCreators.apiGet(options))
            .then((response: IContactLookupDataResponse) => {
                if (response.data.success) {
                    dispatch(ActionCreators.setContactLookupData(response.data.results));
                }

                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },
    setContactLookupData: (lookupData: ILookupData) => {
        return {
            type: ActionTypes.SET_CONTACT_LOOKUP_DATA,
            lookupData
        };
    },

    getContactResults: (centerId: number, contactEntityTypeId: number, roles: string, search: string) => (dispatch: Dispatch<IApplicationState>) => {
        let options: IGet = {
            url: __API__ + '/contact/export-outlook-contacts?centerId=' + centerId + '&contactEntityTypeId=' + contactEntityTypeId + '&roles=' + roles + '&search=' + search,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    getContactResultsForLogistics: (clusterId: number) => (dispatch: Dispatch<IApplicationState>) => {
        let options: IGet = {
            url: __API__ + '/contact/export-outlook-contacts-for-logistics?clusterId=' + clusterId,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    saveNewContactFromTicketing: (firstName: string, lastName: string, email: string, phoneNumber: string) => (dispatch: Dispatch<IApplicationState>): Promise<IContactResponse> => {
        let options: IPost = {
            url: __API__ + '/contact/ticketing',
            data: {
                firstName,
                lastName,
                email,
                phoneNumber
            }
        };

        return dispatch(AuthActionCreators.apiPost(options))
            .then((response: IContactResponse) => {
                return Promise.resolve(response);
            })
            .catch((error: AxiosError) => {
                throw error;
            });
    },

    exportContacts: (centerId: number, contactEntityType: number, adminRoles: string[], centerRole: number, search: string) => async (dispatch: Dispatch<IApplicationState>) => {
        let options: IPost = {
            url: __API__ + '/contact/export',
            data: {
                centerId,
                contactEntityType,
                adminRoles,
                centerRole,
                search
            },
            responseType: 'blob'
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    }
}

export interface IContactState {
    list: IContactForList[],
    lookupData: ILookupData,
    positions: IListItem[],
    info: IContact
}

var initialState: IContactState = {
    list: [],
    positions: [],
    lookupData: {
        phoneTypes: [],
        mobileProviders: [],
        emailFlags: [],
        emailFlagGroups: [],
        contactEntityTypes: [],
        positions: [],
        swapCouriers: [],
        centerAddresses: []
    },
    info: null
};

interface ISetContactList {
    type: string,
    list: IContactForList[]
}

interface ISetContactLookupData {
    type: string,
    lookupData: ILookupData
}

interface ISetContact {
    type: string,
    contact: IContact
}

interface ISetContactPositionList {
    type: string,
    list: IListItem[]
}

const setContactList = (state: IContactState, action: ISetContactList) => {
    state.list = action.list;
    return state;
}

const setContactLookupData = (state: IContactState, action: ISetContactLookupData) => {
    state.lookupData = action.lookupData;
    return state;
}

const setContact = (state: IContactState, action: ISetContact) => {
    state.info = action.contact;
    return state;
}

const setContactPositionList = (state: IContactState, action: ISetContactPositionList) => {
    state.positions = action.list;
    return state;
}

const createReducer = (initialState: IContactState, handlers: any) => {
    return function reducer(state = initialState, action: any) {
        if (handlers.hasOwnProperty(action.type)) {
            let s = Object.assign({}, state);
            return handlers[action.type](s, action);
        } else {
            return state;
        }
    }
}

const handlers = {
    [ActionTypes.SET_CONTACT_LIST]: setContactList,
    [ActionTypes.SET_CONTACT_LOOKUP_DATA]: setContactLookupData,
    [ActionTypes.SET_CONTACT_POSITION_LIST]: setContactPositionList,
    [ActionTypes.SET_CONTACT_INFO]: setContact
};

export const Reducer = createReducer(initialState, handlers);
